import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import PhotoModal from './PhotoModal'
import { getBaseRelationshipType, getRelationshipBasedOnSex } from '../utils/relationshipMappings'
import { differenceInYears, parseISO, format } from 'date-fns'

const { FiEdit, FiTrash2, FiUsers, FiHeart, FiHome, FiBriefcase, FiChevronDown, FiChevronUp, FiUser, FiMapPin, FiCalendar, FiFacebook, FiLinkedin, FiTwitter, FiInstagram, FiEye, FiGrid, FiShare2, FiPlusCircle } = FiIcons

const getRelationshipWeight = (baseType, isFamily) => {
  if (!isFamily) return 1000;
  const weights = {
    'spouse': 1, 'child': 2, 'parent': 3, 'sibling': 4, 'step_child': 5,
    'step_parent': 6, 'step_sibling': 7, 'half_sibling': 8, 'grandparent': 9,
    'grandchild': 10, 'sibling_child': 11, 'parental_sibling': 12,
    'parent_in_law': 13, 'child_in_law': 14, 'sibling_in_law': 15,
    'cousin': 16, 'parental_sibling_in_law': 17, 'sibling_child_in_law': 18,
    'cousin_in_law': 19, 'great_grandparent': 20, 'great_grandchild': 21,
    'grandparent_sibling': 22, 'grandsibling_child': 23, 'cousin_once_removed': 24,
    'second_cousin': 25, 'second_cousin_once_removed': 26, 'third_cousin': 27
  };
  return weights[baseType] || 99;
};

const PersonCard = ({
  person,
  relationships = [],
  people = [],
  viewMode,
  onViewModeChange,
  onEdit,
  onDelete,
  onViewRelationships,
  onNavigateToPerson,
  onDeleteRelationship,
  onEditRelationship,
}) => {
  const [showRelationships, setShowRelationships] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [showPhotoModal, setShowPhotoModal] = useState(false)

  const proximityValue = person.proximity || 'Medium'

  const getSexIcon = (sex) => {
    switch (sex) {
      case 'male': return '♂️'
      case 'female': return '♀️'
      default: return '⚧️'
    }
  }

  const getSexColor = (sex) => {
    switch ((sex || '').toLowerCase()) {
      case 'male': return 'bg-blue-500'
      case 'female': return 'bg-pink-500'
      default: return 'bg-purple-500'
    }
  }

  const getProximityColor = (proximity) => {
    switch ((proximity || '').toLowerCase()) {
      case 'close': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800'
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800'
      case 'far': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800'
      default: return 'bg-gray-100 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700'
    }
  }

  const getFamilyIcon = (relationshipType) => {
    const familyTypes = ['father', 'mother', 'son', 'daughter', 'brother', 'sister', 'grandfather', 'grandmother', 'spouse', 'husband', 'wife'];
    if (familyTypes.some(t => relationshipType.toLowerCase().includes(t))) return FiHome
    if (relationshipType.toLowerCase().includes('friend')) return FiHeart
    if (['colleague', 'boss', 'employee'].includes(relationshipType.toLowerCase())) return FiBriefcase
    return FiUsers
  }

  const calculateAge = (dob) => {
    if (!dob) return null
    try {
      const birthDate = parseISO(dob)
      return differenceInYears(new Date(), birthDate)
    } catch (e) {
      return null
    }
  }

  const formatBirthday = (dob) => {
    if (!dob) return null
    try {
      return format(parseISO(dob), 'MMM d, yyyy')
    } catch (e) {
      return dob
    }
  }

  const normalizeUrl = (url) => {
    if (!url) return '';
    const trimmed = url.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    if (trimmed.startsWith('//')) return `https:${trimmed}`;
    return `https://${trimmed}`;
  }

  const age = calculateAge(person.dob)
  const birthdayLabel = formatBirthday(person.dob)

  const relationshipDetails = relationships.map((rel) => {
    const isPersonA = rel.person_a_id === person.id
    const otherPersonId = isPersonA ? rel.person_b_id : rel.person_a_id
    const otherPerson = people.find((p) => p.id === otherPersonId)
    
    if (!otherPerson) return null;

    const otherPersonName = otherPerson.name
    const otherPersonSex = otherPerson.sex || 'other'
    let relType = isPersonA ? rel.relationship_type_b : rel.relationship_type
    
    const baseType = getBaseRelationshipType(relType)
    const finalType = getRelationshipBasedOnSex(baseType, otherPersonSex)

    return {
      id: otherPersonId,
      relId: rel.id,
      name: otherPersonName,
      type: finalType || relType,
      baseType: baseType,
      icon: getFamilyIcon(finalType || relType),
      photo_url: otherPerson.photo_url,
      sex: otherPerson.sex,
      is_family: otherPerson.is_family,
      nicknames: otherPerson.nicknames,
      notes: otherPerson.notes
    }
  }).filter(Boolean).sort((a, b) => {
    const weightA = getRelationshipWeight(a.baseType, a.is_family);
    const weightB = getRelationshipWeight(b.baseType, b.is_family);
    if (weightA !== weightB) return weightA - weightB;
    const lastNameA = (a.name || '').trim().split(' ').pop() || '';
    const lastNameB = (b.name || '').trim().split(' ').pop() || '';
    if (lastNameA !== lastNameB) return lastNameA.localeCompare(lastNameB);
    return a.name.localeCompare(b.name);
  })

  return (
    <>
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-800 p-5 hover:shadow-xl transition-all duration-300 relative group overflow-hidden flex flex-col h-fit self-start w-full"
        id={`person-${person.id}`}
      >
        <div className={`absolute top-0 left-0 w-full h-1 ${getSexColor(person.sex)}`} />
        
        {/* Profile Header */}
        <div className="flex items-center space-x-4 mb-4 flex-shrink-0">
          <div 
            className="relative w-16 h-16 rounded-2xl overflow-hidden bg-gray-50 dark:bg-slate-800 border-2 border-gray-100 dark:border-slate-700 cursor-pointer shadow-sm group-hover:border-blue-400 dark:group-hover:border-blue-500 transition-colors flex-shrink-0"
            onClick={() => person.photo_url && !imageError && setShowPhotoModal(true)}
          >
            {person.photo_url && !imageError ? (
              <img src={person.photo_url} alt={person.name} className="w-full h-full object-cover" onError={() => setImageError(true)} />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <SafeIcon icon={FiUser} className="text-gray-300 dark:text-slate-600 text-3xl" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 
              className="text-xl font-black text-gray-900 dark:text-white leading-tight flex items-start cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              onClick={() => onViewRelationships(person)}
            >
              <span className="mr-2 opacity-70 text-base mt-1">{getSexIcon(person.sex)}</span>
              <span className="break-words">{person.name}</span>
            </h3>

            <div className="flex flex-wrap gap-1.5 mt-1.5">
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider border ${getProximityColor(proximityValue)}`}>
                {proximityValue}
              </span>
              {person.is_family && (
                <span className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider border border-indigo-100 dark:border-indigo-800 flex items-center">
                  <SafeIcon icon={FiHome} className="mr-1" size={10} /> Family
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Unified Full-Width Action Bar */}
        <div className="flex items-center w-full mb-4 opacity-100 transition-all duration-200">
          <div className="flex-1 flex bg-gray-50/50 dark:bg-slate-800/50 p-1 rounded-xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden">
            <button 
              onClick={() => onViewModeChange('tree', person.id)} 
              className={`flex-1 flex justify-center py-2 rounded-lg transition-all ${viewMode === 'tree' ? 'bg-white dark:bg-slate-700 shadow-sm text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200 hover:bg-white/50 dark:hover:bg-slate-700/50'}`}
              title="Tree View"
            >
              <SafeIcon icon={FiShare2} size={18} />
            </button>
            <div className="w-px h-6 bg-gray-200 dark:bg-slate-700 self-center mx-1" />
            <button
              onClick={() => onViewRelationships(person, { showAddForm: true })}
              className="flex-1 flex justify-center py-2 text-gray-400 dark:text-slate-400 hover:text-purple-600 dark:hover:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-all"
              title="Add Connection"
            >
              <SafeIcon icon={FiPlusCircle} size={20} />
            </button>
            <button
              onClick={() => onEdit(person)}
              className="flex-1 flex justify-center py-2 text-gray-400 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-all"
              title="Edit"
            >
              <SafeIcon icon={FiEdit} size={20} />
            </button>
            <button
              onClick={() => onDelete(person)}
              className="flex-1 flex justify-center py-2 text-gray-400 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
              title="Delete"
            >
              <SafeIcon icon={FiTrash2} size={20} />
            </button>
          </div>
        </div>

        <div className="flex flex-col flex-1">
          <div className="space-y-3">
            {(birthdayLabel || person.nicknames) && (
              <div className="bg-gray-50 dark:bg-slate-800/50 rounded-xl p-3 space-y-2">
                {birthdayLabel && (
                  <div className="flex items-center text-sm text-gray-600 dark:text-slate-300">
                    <SafeIcon icon={FiCalendar} className="mr-2 text-blue-500 dark:text-blue-400" size={14} />
                    <span>{birthdayLabel} {age !== null && <span className="font-semibold text-gray-400 dark:text-slate-500 ml-1">({age} yrs)</span>}</span>
                  </div>
                )}
                {person.nicknames && (
                  <div className="flex items-start text-sm text-gray-600 dark:text-slate-300">
                    <SafeIcon icon={FiUser} className="mr-2 mt-1 text-purple-500 dark:text-purple-400" size={14} />
                    <span className="italic">"{person.nicknames}"</span>
                  </div>
                )}
              </div>
            )}

            {person.notes && (
              <div className="pt-3 border-t border-gray-100 dark:border-slate-800">
                <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed line-clamp-2 italic">{person.notes}</p>
              </div>
            )}

            {/* Secondary Notes (from connections) */}
            {relationshipDetails.some(rel => rel.notes) && (
              <div className={`pt-3 ${(person.notes || birthdayLabel || person.nicknames) ? 'border-t border-gray-100 dark:border-slate-800' : ''} space-y-2`}>
                {relationshipDetails.filter(rel => rel.notes).map(rel => (
                  <div key={`sec-note-${rel.relId}`} className="bg-indigo-50/60 dark:bg-indigo-900/20 p-2.5 rounded-xl border border-indigo-100/60 dark:border-indigo-800/50 relative overflow-hidden">
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-indigo-300 dark:bg-indigo-600"></div>
                    <p className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider mb-1 flex items-center">
                      <SafeIcon icon={FiUsers} className="mr-1.5" size={10} />
                      {rel.name}'s Note ({rel.type})
                    </p>
                    <p className="text-xs text-gray-600 dark:text-slate-300 leading-relaxed line-clamp-2 italic">"{rel.notes}"</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-auto pt-4 flex-shrink-0">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="flex items-center space-x-3 px-1">
                {person.facebook_url && (
                  <a href={normalizeUrl(person.facebook_url)} target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:scale-125 transition-transform">
                    <SafeIcon icon={FiFacebook} size={18} />
                  </a>
                )}
                {person.linkedin_url && (
                  <a href={normalizeUrl(person.linkedin_url)} target="_blank" rel="noopener noreferrer" className="text-blue-700 dark:text-blue-500 hover:scale-125 transition-transform">
                    <SafeIcon icon={FiLinkedin} size={18} />
                  </a>
                )}
                {person.instagram_url && (
                  <a href={normalizeUrl(person.instagram_url)} target="_blank" rel="noopener noreferrer" className="text-pink-600 dark:text-pink-400 hover:scale-125 transition-transform">
                    <SafeIcon icon={FiInstagram} size={18} />
                  </a>
                )}
                {person.twitter_url && (
                  <a href={normalizeUrl(person.twitter_url)} target="_blank" rel="noopener noreferrer" className="text-gray-800 dark:text-slate-300 hover:scale-125 transition-transform">
                    <SafeIcon icon={FiTwitter} size={18} />
                  </a>
                )}
              </div>
            </div>

            {relationshipDetails.length > 0 && (
              <div>
                <button
                  onClick={(e) => { e.stopPropagation(); setShowRelationships(!showRelationships); }}
                  className="flex items-center justify-between w-full py-2.5 px-3 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-700 hover:border-blue-200 dark:hover:border-blue-800 hover:bg-blue-50 dark:hover:bg-slate-700/50 text-xs transition-all shadow-sm rounded-xl"
                >
                  <div className="flex items-center text-gray-600 dark:text-slate-300 font-semibold">
                    <SafeIcon icon={FiUsers} className="mr-2 text-blue-500 dark:text-blue-400" size={14} />
                    {relationshipDetails.length} Connections
                  </div>
                  <SafeIcon icon={showRelationships ? FiChevronUp : FiChevronDown} className="text-gray-400 dark:text-slate-500" size={14} />
                </button>
                <AnimatePresence>
                  {showRelationships && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="pt-2 pb-1 space-y-1.5 pl-1">
                        {relationshipDetails.map((rel) => (
                          <div key={rel.relId} className="flex items-center justify-between group/rel cursor-pointer p-2 rounded-xl hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors border border-transparent hover:border-gray-100 dark:hover:border-slate-700" onClick={(e) => { e.stopPropagation(); onNavigateToPerson(rel.id); }}>
                            <div className="flex items-start space-x-3 min-w-0 flex-1">
                              <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-100 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 flex-shrink-0 mt-0.5">
                                {rel.photo_url ? <img src={rel.photo_url} alt={rel.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-400 dark:text-slate-500">{rel.name.charAt(0)}</div>}
                              </div>
                              <div className="flex flex-col min-w-0 flex-1">
                                <div className="flex items-baseline space-x-1.5 min-w-0">
                                  <span className="text-[13px] text-gray-700 dark:text-slate-200 font-bold break-words">{rel.name}</span>
                                  {rel.nicknames && <span className="text-[10px] text-gray-400 dark:text-slate-500 italic break-words">"{rel.nicknames}"</span>}
                                </div>
                                <div className="flex items-center space-x-1 flex-wrap gap-y-1">
                                  <span className="text-[10px] bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-md font-medium capitalize border border-blue-100/50 dark:border-blue-800/50">{rel.type}</span>
                                  {rel.is_family && <span className="text-[10px] bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 px-1.5 py-0.5 rounded-md font-medium border border-indigo-100/50 dark:border-indigo-800/50">Family</span>}
                                </div>
                                {rel.notes && <p className="text-[10px] text-gray-500 dark:text-slate-400 truncate mt-0.5 italic">{rel.notes}</p>}
                              </div>
                            </div>
                            <div className="flex items-center space-x-1 opacity-100 transition-opacity flex-shrink-0 ml-2">
                              <button onClick={(e) => { e.stopPropagation(); onEditRelationship && onEditRelationship({ id: rel.relId, otherPersonName: rel.name, otherPersonId: rel.id, currentRelationshipType: rel.type }, person); }} className="p-1.5 text-blue-500 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-md"><SafeIcon icon={FiEdit} size={14} /></button>
                              <button onClick={(e) => { e.stopPropagation(); onDeleteRelationship && onDeleteRelationship(rel.relId); }} className="p-1.5 text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md"><SafeIcon icon={FiTrash2} size={14} /></button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showPhotoModal && <PhotoModal person={person} isOpen={showPhotoModal} onClose={() => setShowPhotoModal(false)} />}
      </AnimatePresence>
    </>
  )
}
export default PersonCard